/**
 * Persona Selector v2.0
 * 
 * A premium, clean interface for selecting AI persona and response depth.
 * Designed for a professional experience without emoji overload.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { 
    X, 
    Check,
    ChevronRight,
    Lock,
    Zap,
    BookOpen,
    Briefcase,
    MessageSquare,
    Layers,
    Crown
} from 'lucide-react';
import { 
    PERSONAS, 
    DEPTHS, 
    QUICK_PRESETS,
    PersonaId, 
    ResponseDepth,
    canAccessPersona,
    canAccessDepth,
    getCombinedMode,
    calculateTokenCost
} from '../config/aiPersonaConfig';

import { GlobalConfig } from '../types';

interface PersonaSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    currentPersona: PersonaId;
    currentDepth: ResponseDepth;
    onSelect: (persona: PersonaId, depth: ResponseDepth) => void;
    userTier: 'free' | 'premium' | 'max';
    isDarkMode?: boolean;
    config?: GlobalConfig; // For displaying accurate token costs
}

const PersonaSelector: React.FC<PersonaSelectorProps> = ({
    isOpen,
    onClose,
    currentPersona,
    currentDepth,
    onSelect,
    userTier,
    isDarkMode = true,
    config
}) => {
    const [selectedPersona, setSelectedPersona] = useState<PersonaId>(currentPersona);
    const [selectedDepth, setSelectedDepth] = useState<ResponseDepth>(currentDepth);
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    const combinedMode = useMemo(() => 
        getCombinedMode(selectedPersona, selectedDepth), 
        [selectedPersona, selectedDepth]
    );
    
    const handleApply = useCallback(() => {
        onSelect(selectedPersona, selectedDepth);
        onClose();
    }, [selectedPersona, selectedDepth, onSelect, onClose]);
    
    const handlePresetSelect = useCallback((preset: typeof QUICK_PRESETS[0]) => {
        if (canAccessPersona(preset.personaId, userTier) && canAccessDepth(preset.depthId, userTier)) {
            setSelectedPersona(preset.personaId);
            setSelectedDepth(preset.depthId);
        }
    }, [userTier]);
    
    if (!isOpen) return null;
    
    const bgColor = isDarkMode ? 'bg-slate-900' : 'bg-white';
    const textColor = isDarkMode ? 'text-white' : 'text-slate-900';
    const textMuted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
    const borderColor = isDarkMode ? 'border-slate-700' : 'border-slate-200';
    const cardBg = isDarkMode ? 'bg-slate-800' : 'bg-slate-50';
    const cardHover = isDarkMode ? 'hover:bg-slate-750' : 'hover:bg-slate-100';
    
    const personaIcons: Record<PersonaId, React.ReactNode> = {
        general: <MessageSquare size={20} />,
        educator: <BookOpen size={20} />,
        lawyer: <Briefcase size={20} />
    };
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div 
                className={`
                    relative w-full max-w-lg rounded-2xl shadow-2xl
                    ${bgColor} ${textColor}
                    max-h-[90vh] overflow-hidden flex flex-col
                    animate-in fade-in zoom-in-95 duration-200
                `}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={`p-5 border-b ${borderColor}`}>
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold">Response Settings</h2>
                            <p className={`text-sm ${textMuted}`}>
                                Customize how Luca responds to you
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-lg transition-colors ${
                                isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'
                            }`}
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>
                
                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6">
                    {/* Quick Presets */}
                    <div>
                        <h3 className={`text-sm font-medium mb-3 ${textMuted}`}>
                            Quick Presets
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            {QUICK_PRESETS.map(preset => {
                                const isAccessible = canAccessPersona(preset.personaId, userTier) && 
                                                   canAccessDepth(preset.depthId, userTier);
                                const isSelected = selectedPersona === preset.personaId && 
                                                  selectedDepth === preset.depthId;
                                
                                return (
                                    <button
                                        key={preset.id}
                                        onClick={() => handlePresetSelect(preset)}
                                        disabled={!isAccessible}
                                        className={`
                                            relative p-4 rounded-xl text-left transition-all
                                            ${isSelected 
                                                ? 'ring-2 ring-blue-500 bg-blue-500/10' 
                                                : `${cardBg} ${cardHover}`}
                                            ${!isAccessible ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        {!isAccessible && (
                                            <div className="absolute top-2 right-2">
                                                <Lock size={14} className={textMuted} />
                                            </div>
                                        )}
                                        <div className="font-medium text-sm">
                                            {preset.name}
                                        </div>
                                        <div className={`text-xs mt-1 ${textMuted}`}>
                                            {preset.description}
                                        </div>
                                        {isSelected && (
                                            <div className="absolute bottom-2 right-2">
                                                <Check size={16} className="text-blue-500" />
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    
                    {/* Advanced Options Toggle */}
                    <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className={`
                            w-full flex items-center justify-between p-3 rounded-lg
                            ${cardBg} ${cardHover} transition-colors
                        `}
                    >
                        <span className={`text-sm font-medium ${textMuted}`}>
                            Advanced Settings
                        </span>
                        <ChevronRight 
                            size={18} 
                            className={`transition-transform ${showAdvanced ? 'rotate-90' : ''}`}
                        />
                    </button>
                    
                    {/* Advanced Options */}
                    {showAdvanced && (
                        <div className="space-y-5">
                            {/* Persona Selection */}
                            <div>
                                <h3 className={`text-sm font-medium mb-3 ${textMuted}`}>
                                    Persona
                                </h3>
                                <div className="space-y-2">
                                    {Object.values(PERSONAS).map(persona => {
                                        const isAccessible = canAccessPersona(persona.id, userTier);
                                        const isSelected = selectedPersona === persona.id;
                                        
                                        return (
                                            <button
                                                key={persona.id}
                                                onClick={() => isAccessible && setSelectedPersona(persona.id)}
                                                disabled={!isAccessible}
                                                className={`
                                                    w-full flex items-center gap-4 p-4 rounded-xl transition-all
                                                    ${isSelected 
                                                        ? 'ring-2 bg-opacity-10' 
                                                        : `${cardBg} ${cardHover}`}
                                                    ${!isAccessible ? 'opacity-50 cursor-not-allowed' : ''}
                                                `}
                                                style={{
                                                    borderColor: isSelected ? persona.accentColor : 'transparent',
                                                    backgroundColor: isSelected ? `${persona.accentColor}15` : undefined
                                                }}
                                            >
                                                <div 
                                                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                                                    style={{ backgroundColor: persona.accentColor }}
                                                >
                                                    {personaIcons[persona.id]}
                                                </div>
                                                <div className="flex-1 text-left">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{persona.name}</span>
                                                        {persona.minimumTier !== 'free' && (
                                                            <span className={`
                                                                text-[10px] px-1.5 py-0.5 rounded font-medium
                                                                ${persona.minimumTier === 'premium' 
                                                                    ? 'bg-amber-500/20 text-amber-500' 
                                                                    : 'bg-purple-500/20 text-purple-500'}
                                                            `}>
                                                                {persona.minimumTier.toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className={`text-xs ${textMuted}`}>
                                                        {persona.tagline}
                                                    </p>
                                                </div>
                                                {isSelected ? (
                                                    <Check size={18} style={{ color: persona.accentColor }} />
                                                ) : !isAccessible ? (
                                                    <Lock size={18} className={textMuted} />
                                                ) : null}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            
                            {/* Depth Selection */}
                            <div>
                                <h3 className={`text-sm font-medium mb-3 ${textMuted}`}>
                                    Response Depth
                                </h3>
                                <div className="space-y-2">
                                    {Object.values(DEPTHS).map(depth => {
                                        const isAccessible = canAccessDepth(depth.id, userTier);
                                        const isSelected = selectedDepth === depth.id;
                                        
                                        return (
                                            <button
                                                key={depth.id}
                                                onClick={() => isAccessible && setSelectedDepth(depth.id)}
                                                disabled={!isAccessible}
                                                className={`
                                                    w-full flex items-center gap-4 p-4 rounded-xl transition-all
                                                    ${isSelected 
                                                        ? 'ring-2 ring-blue-500 bg-blue-500/10' 
                                                        : `${cardBg} ${cardHover}`}
                                                    ${!isAccessible ? 'opacity-50 cursor-not-allowed' : ''}
                                                `}
                                            >
                                                <div className={`
                                                    w-10 h-10 rounded-lg flex items-center justify-center
                                                    ${isSelected ? 'bg-blue-500 text-white' : `${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'} ${textMuted}`}
                                                `}>
                                                    <Layers size={20} />
                                                </div>
                                                <div className="flex-1 text-left">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{depth.name}</span>
                                                        {depth.minimumTier === 'premium' && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-500/20 text-amber-500">
                                                                PREMIUM
                                                            </span>
                                                        )}
                                                        {depth.minimumTier === 'max' && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-purple-500/20 text-purple-500">
                                                                MAX
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className={`text-xs ${textMuted}`}>
                                                        {depth.description}
                                                    </p>
                                                </div>
                                                {isSelected ? (
                                                    <Check size={18} className="text-blue-500" />
                                                ) : !isAccessible ? (
                                                    <Lock size={18} className={textMuted} />
                                                ) : null}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Current Selection Summary with Enhanced Token Display */}
                    <div className={`p-4 rounded-xl border-2 ${cardBg}`} style={{ borderColor: isDarkMode ? '#334155' : '#e2e8f0' }}>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className={`text-xs font-medium ${textMuted} mb-1`}>
                                        Current Mode
                                    </div>
                                    <div className="font-semibold text-base">
                                        {combinedMode.displayName}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className={`text-xs font-medium ${textMuted} mb-1`}>
                                        Token Cost
                                    </div>
                                    <div className="flex items-center gap-1.5 font-bold text-base">
                                        <Zap size={16} className="text-amber-500" />
                                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
                                            {combinedMode.effectiveMultiplier === 1.0 
                                                ? 'Standard'
                                                : `${combinedMode.effectiveMultiplier.toFixed(1)}x`}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className={`text-xs ${textMuted} pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                <div className="flex items-start gap-1.5">
                                    <span className="font-medium">Best for:</span>
                                    <span>{combinedMode.bestFor}</span>
                                </div>
                            </div>
                            {config && (
                                <div className={`text-[10px] ${textMuted} pt-2 space-y-1 border-t ${isDarkMode ? 'border-slate-700/50' : 'border-slate-200'}`}>
                                    <div>💡 Base token refresh: {userTier === 'free' ? config.standardRefreshRate || 800 : userTier === 'premium' ? config.premiumRefreshRate || 5000 : config.maxRefreshRate || 20000}/hour</div>
                                    <div>⚡ Example: {Math.round((config.minBudgetPerRequest || 300) * combinedMode.effectiveMultiplier)} - {Math.round((config.maxBudgetPerRequest || 125000) * combinedMode.effectiveMultiplier)} tokens per message</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Footer */}
                <div className={`p-5 border-t ${borderColor}`}>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className={`
                                flex-1 py-3 px-4 rounded-xl font-medium transition-colors
                                ${isDarkMode ? 'bg-slate-800 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200'}
                            `}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="flex-1 py-3 px-4 rounded-xl font-medium bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                        >
                            Apply Settings
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PersonaSelector;

